import Quill from 'quill';
import { RefObject, useRef } from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

import { isEmptyHtml } from '@/caregiverSpace/shared/lib';
import { CaregiverPhraseDto } from '@/caregiverSpace/shared/model';

import { QuillEditor } from './QuillEditor';

export type EditorProps<TFieldValues extends FieldValues> = {
    signature?: string;
    name: Path<TFieldValues>;
    placeholder?: string;
    required?: string;
    id?: string;
    mode?: 'html' | 'plaintext';
    quillRef?: RefObject<Quill | null>;
    contentInsertMode?: 'innerHTML' | 'paste';
    phrases?: CaregiverPhraseDto[];
    onPhraseSelect?: (phrase: CaregiverPhraseDto) => string;
};

const isEmptyPlaintext = (text: string): boolean => {
    return !text || text.trim() === '';
};

export const Editor = <TFieldValues extends FieldValues>({
    signature,
    name,
    placeholder,
    required,
    id,
    mode = 'html',
    quillRef: externalQuillRef,
    contentInsertMode = 'innerHTML',
    phrases,
    onPhraseSelect,
}: EditorProps<TFieldValues>) => {
    const { control, getValues } = useFormContext<TFieldValues>();

    const internalQuillRef = useRef<Quill | null>(null);
    const quillRef = externalQuillRef ?? internalQuillRef;
    const defaultValue = getValues(name);

    const validateValue = (value: string) => {
        if (mode === 'plaintext') {
            return !isEmptyPlaintext(value) || required;
        }
        return !isEmptyHtml(value) || required;
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{
                validate: validateValue,
            }}
            render={({ field }) => (
                <QuillEditor
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    ref={quillRef}
                    placeholder={placeholder}
                    signature={signature}
                    id={id}
                    contentInsertMode={contentInsertMode}
                    phrases={phrases}
                    onPhraseSelect={onPhraseSelect}
                    plainTextOutput={mode === 'plaintext'}
                />
            )}
        />
    );
};
